import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, PhoneIcon, LinkedinIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
const staggerContainer = {
  initial: {
    opacity: 0
  },
  whileInView: {
    opacity: 1
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    staggerChildren: 0.2
  }
};
const team = [{
  name: 'Charles McErick',
  position: 'Managing Partner',
  areas: 'Corporate Law, Commercial Litigation',
  email: 'charles@mcerickattorneys.co.bw',
  phone: '(+267) 7255 3210',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=800',
  bio: "With over 15 years of experience in corporate law, Charles leads the firm's commercial litigation department. He has successfully represented major corporations in complex disputes and advises on high-value commercial transactions."
}, {
  name: 'Sarah Tlale',
  position: 'Partner',
  areas: 'Conveyancing, Property Law',
  email: 'sarah@mcerickattorneys.co.bw',
  phone: '(+267) 7123 4567',
  image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800',
  bio: 'Sarah heads our Conveyancing department. Her meticulous attention to detail and deep understanding of property law ensures smooth transactions for both residential and commercial property deals.'
}, {
  name: 'David Kgosi',
  position: 'Senior Associate',
  areas: 'Family Law, Civil Litigation',
  email: 'david@mcerickattorneys.co.bw',
  phone: '(+267) 7345 6789',
  image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&q=80&w=800',
  bio: 'David specializes in family law and civil litigation. He is known for his compassionate yet strategic approach to sensitive family matters, including divorce, custody, and maintenance disputes.'
}, {
  name: 'Lerato Modise',
  position: 'Associate',
  areas: 'Labour Law, Employment Disputes',
  email: 'lerato@mcerickattorneys.co.bw',
  phone: '(+267) 7456 7890',
  image: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?auto=format&fit=crop&q=80&w=800',
  bio: 'Lerato is a dedicated associate focusing on labour law. She regularly advises both employers and employees on workplace disputes, disciplinary hearings, and compliance with employment legislation.'
}];
export function Team() {
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-458">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-459">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-460">
          <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2000" alt="Professionals collaborating in a modern office" className="w-full h-full object-cover" data-id="element-461" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-462">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-6" data-id="element-463">
            Our Team
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-464">
            Experienced legal professionals dedicated to achieving the best
            outcomes for our clients.
          </motion.p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-24" data-id="element-465">
        <div className="max-w-container mx-auto px-6" data-id="element-466">
          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-467">
            {team.map((member, idx) => <motion.div key={idx} variants={fadeInUp} className="bg-white rounded-2xl overflow-hidden shadow-soft flex flex-col sm:flex-row" data-id="element-468">
                <div className="sm:w-2/5 h-64 sm:h-auto relative" data-id="element-469">
                  <img src={member.image} alt={member.name} className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-470" />
                </div>
                <div className="sm:w-3/5 p-8 flex flex-col justify-center" data-id="element-471">
                  <h3 className="font-serif text-2xl font-bold text-brand-charcoal mb-1" data-id="element-472">
                    {member.name}
                  </h3>
                  <p className="text-brand-gold font-medium mb-4" data-id="element-473">
                    {member.position}
                  </p>
                  <p className="text-sm text-gray-500 mb-4 font-medium uppercase tracking-wider" data-id="element-474">
                    {member.areas}
                  </p>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed" data-id="element-475">
                    {member.bio}
                  </p>
                  <div className="space-y-2 mt-auto pt-4 border-t border-gray-100" data-id="element-476">
                    <a href={`mailto:${member.email}`} className="flex items-center text-sm text-gray-600 hover:text-brand-green transition-colors" data-id="element-477">
                      <MailIcon className="w-4 h-4 mr-3 text-brand-gold" data-id="element-478" />
                      {member.email}
                    </a>
                    <a href={`tel:${member.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center text-sm text-gray-600 hover:text-brand-green transition-colors" data-id="element-479">
                      <PhoneIcon className="w-4 h-4 mr-3 text-brand-gold" data-id="element-480" />
                      {member.phone}
                    </a>
                    <a href="#" className="flex items-center text-sm text-gray-600 hover:text-brand-green transition-colors" data-id="element-481">
                      <LinkedinIcon className="w-4 h-4 mr-3 text-brand-gold" data-id="element-482" />
                      LinkedIn Profile
                    </a>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>
    </div>;
}